import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react'
import { pick, find, isEmpty, mapValues, isUndefined } from 'lodash'
import { useTranslation } from 'react-i18next'
import InputElement from './InputElement'
import Button from '@ublique/components/Button'
import { Row, Column } from '@ublique/components/Grid'
import { wizardStore } from 'state'
import withStore from 'hoc/withStore'
import { validateFields } from 'utils/validation'
import {
  chunks,
  getValueState,
  isFormValid
} from 'components/Wizard/helpers/wizard-item'
import { CLONE_SCENARIO, FORM_STATUS } from 'components/Wizard/constants'
import { updatePayloadMapper } from '../helpers/container'

const WizardItem = ({
  wizard,
  values,
  setData,
  conditions,
  stepNumber,
  stepConfig,
  changeOpen,
  progressSubmit,
  progressBarStep,
  setProgressBarStep,
  wizardSave,
  manageLastStep,
  isScheduledMode
}) => {
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [stepSubmit, setStepSubmit] = useState(false)
  const [conditionStates, setConditionStates] = useState({})

  const { t } = useTranslation(['Wizard', 'WizardPlatform'])
  const { clone, creationMode, inputElements, scenarioType } = wizard

  const info = ['inputs', 'configurations']

  const getInitialState = (dataInit) => {
    const state = {}
    const elements = {}

    for (const input of dataInit) {
      state[input.id] = getValueState({ values, input, clone })
      elements[input.id] = input
    }

    return { state, elements }
  }

  const validateWizardItem = (validationData) => {
    if (!isEmpty(validationData) && !isEmpty(inputElements)) {
      let toValidate = mapValues(validationData, (value, key) => {
        if (['date', 'datetime'].includes(inputElements[key]?.type)) {
          const date = new Date(value).getTime()
          return isNaN(date) ? null : date
        }

        if (['select', 'radio'].includes(inputElements[key]?.type)) {
          return value?.value
        }

        if (['multiselect'].includes(inputElements[key]?.type)) {
          return value?.length > 0
        }

        if (['checkbox'].includes(inputElements[key]?.type)) {
          const entries = Object.entries(value)
            .filter(([_, pred]) => pred)
            .map(([key]) => key)

          return entries.length > 0
        }

        return value
      })

      toValidate = {
        ...toValidate,
        creationMode,
        scenarioType,
        scenarioStatus: !isEmpty(clone) ? clone.scenarioStatus : ''
      }

      const value = validateFields(
        toValidate,
        pick(conditions, Object.keys(validationData))
      )
      wizardStore.update((prevState) => ({
        ...prevState,
        status: { ...prevState.status, ...value }
      }))
      setConditionStates(value)
    }
  }

  useEffect(() => {
    validateWizardItem(formData)
  }, [formData])

  useEffect(() => {
    let formDataState = {}
    let inputElements = {}

    setLoading(true)

    if (stepConfig && (stepConfig.inputs || stepConfig.configuration)) {
      for (const key of info) {
        if (stepConfig[key]) {
          const { state, elements } = getInitialState(stepConfig[key])

          formDataState = { ...formDataState, ...state }
          inputElements = { ...inputElements, ...elements }
        }
      }
    }

    if (!isEmpty(stepConfig.sections)) {
      for (const section of stepConfig.sections) {
        const { state, elements } = getInitialState(section.inputs)
        formDataState = { ...formDataState, ...state }
        inputElements = { ...inputElements, ...elements }
      }
    }

    wizardStore.update((oldState) => ({
      ...oldState,
      inputElements: {
        ...oldState.inputElements,
        ...inputElements
      }
    }))

    setFormData(formDataState)
    setLoading(false)
  }, [stepConfig])

  useEffect(() => {
    if (Number.isInteger(progressBarStep)) {
      const isWizardFormValid = isFormValid(conditionStates)
      if (isWizardFormValid) {
        setStepSubmit(true)

        if (isWizardFormValid) {
          setData(formData)
        } else {
          setProgressBarStep(null)
        }

        progressSubmit(isWizardFormValid)
      } else {
        setStepSubmit(false)
        if (progressBarStep < stepNumber) {
          setStepSubmit(true)
          progressSubmit(true)
        }
      }
    }
  }, [progressBarStep])

  const setValue = (id, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: value
    }))
  }

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault()
      setSubmitted(true)

      if (isFormValid(conditionStates)) {
        setData(formData)
        setSubmitted(false)
      }
    },
    [conditionStates, formData, setData]
  )

  const onUpdatePayloadMapper = (type, id, value) => {
    if (type === 'date') {
      updatePayloadMapper(id, value)
    }
  }

  const getLabel = ({ label, id }) => {
    return t(!isUndefined(label) ? label : id)
  }

  const checkState = (elementConditions, state) =>
    !isEmpty(find(elementConditions, (el) => el.state === state))

  const getInputElement = (element) => {
    const className = element.type === 'custom' ? 'full-width' : ''
    let elementStates = {}

    for (const state of FORM_STATUS) {
      elementStates = {
        ...elementStates,
        [state]: checkState(conditionStates[element.id], state)
      }
    }

    elementStates.disabled =
      elementStates.disabled ||
      (creationMode === CLONE_SCENARIO && element.readOnlyWhenClone)

    const equals =
      element.equals &&
      creationMode === CLONE_SCENARIO &&
      element.readOnlyWhenClone
        ? undefined
        : element.equals

    if (elementStates.hidden || elementStates.formHidden) {
      return null
    }

    return (
      <Column key={`column-${element.id}`} className={className}>
        <InputElement
          {...element}
          {...elementStates}
          equals={equals}
          key={element.id}
          setValue={setValue}
          isScheduledMode={isScheduledMode}
          onUpdatePayloadMapper={onUpdatePayloadMapper}
          formValues={formData}
          label={getLabel(element)}
          value={formData[element.id]}
          submitted={submitted || stepSubmit}
          conditionInfo={conditions[element.id]}
          conditions={conditionStates[element.id]}
        />
      </Column>
    )
  }

  const getRowFromChunk = (inputs) => {
    const elements = inputs
      .map((couple) => getInputElement(couple))
      .filter((element) => element)
    if (!elements.length) {
      return null
    }

    return <Row key={`row-${inputs[0].id}`}>{elements}</Row>
  }

  const RecapFooter = ({ t, onCancelClick, onSecondaryClick, onSubmit }) => (
    <Row className='buttons-footer'>
      <a onClick={onCancelClick}>{t('WizardPlatform:SecondaryButtonLabel')}</a>
      <Button kind='secondary' onClick={onSecondaryClick}>
        {t('WizardPlatform:Back')}
      </Button>
      <Button onClick={onSubmit}>{t('WizardPlatform:SaveButtonLabel')}</Button>
    </Row>
  )

  const renderButtons = useMemo(
    () => (
      <Row className='buttons-footer'>
        <a onClick={() => changeOpen(false)}>
          {t('WizardPlatform:SecondaryButtonLabel')}
        </a>

        {stepNumber > 0 && (
          <Button
            kind='secondary'
            onClick={() => setProgressBarStep(stepNumber - 1)}
          >
            {t('WizardPlatform:Back')}
          </Button>
        )}

        <Button type='submit'>{t('WizardPlatform:PrimaryButtonLabel')}</Button>
      </Row>
    ),
    [t, changeOpen, stepNumber]
  )

  return (
    !loading && (
      <Row>
        <form onSubmit={onSubmit}>
          <div className='overflow-box'>
            {stepConfig &&
              info.map(
                (key) =>
                  stepConfig[key] &&
                  stepConfig[key].length > 0 &&
                  chunks(stepConfig[key]).map(getRowFromChunk)
              )}
            {stepConfig &&
              stepConfig.sections &&
              stepConfig.sections.map((section, idx) => (
                <Fragment key={`section-${section.title}`}>
                  <hr className='section-line' />
                  <h5 className={`section-title section-${idx}`}>
                    {getLabel({ label: section.title, id: section.title })}
                  </h5>

                  {chunks(section.inputs).map(getRowFromChunk)}
                </Fragment>
              ))}
          </div>
          {manageLastStep ? (
            <RecapFooter
              t={t}
              onSubmit={wizardSave}
              onCancelClick={() => changeOpen(false)}
              onSecondaryClick={() => setProgressBarStep(stepNumber - 1)}
            />
          ) : (
            renderButtons
          )}
        </form>
      </Row>
    )
  )
}

export default withStore(React.memo(WizardItem), ['wizard'])
